import React, { useEffect, useState } from "react";
import api from "./api";
import { Action, TeamInfo } from "./types";
import { Box, Button, CircularProgress, Container, Step, StepConnector, StepIcon, Stepper, styled, TextField, Typography } from "@mui/material";
import { Help, CheckCircle, Cancel, DoNotDisturbOn, ChangeCircle } from "@mui/icons-material";
import { removeAllNonAlphaCharacters, removeDiacritics } from "./Utils/utils";
import { useAlerts } from "./Contexts/AlertsContext";
import logo from "./img/logoTextRight.svg";

const CustomStepConnector = styled(StepConnector)(({ theme }) => ({
  [`& .MuiStepConnector-line`]: {
    borderColor: theme.palette.grey[400],
    borderWidth: 3,
  },
  [`&.Mui-active .MuiStepConnector-line`]: {
    borderColor: theme.palette.primary.main,
    borderWidth: 3,
  },
  [`&.Mui-completed .MuiStepConnector-line`]: {
    borderColor: theme.palette.primary.main,
    borderWidth: 3,
  },
}));

function App() {
  const [team, setTeam] = useState<TeamInfo | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [code, setCode] = useState("");

  const alerts = useAlerts();

  useEffect(() => {
    api.teams.loadTeamInfo({ success: setTeam, error: console.log });
  }, []);

  let currentPuzzle = [...(team?.puzzlesStatus || [])].reverse().find((p) => p.status !== "unknown");
  if (currentPuzzle === undefined || currentPuzzle.status === "solved" || currentPuzzle.status === "solvedWithHelp" || currentPuzzle.status === "failed") {
    currentPuzzle = team?.puzzlesStatus?.find((p) => p.status === "unknown");
  }

  useEffect(() => {
    setInstructions("");
    if (currentPuzzle?.status === "unknown") {
      api.puzzles.getInstructions(currentPuzzle.puzzleId, { success: setInstructions, error: console.log });
    }
    // eslint-disable-next-line
  }, [team]);

  const finishedAll = (team?.puzzlesStatus?.length || 0) > 0 && team?.puzzlesStatus?.every((p) => p.status === "solved" || p.status === "solvedWithHelp" || p.status === "failed");
  const score = team?.puzzlesStatus?.map((p) => (p.status === "solved" ? 1 : p.status === "solvedWithHelp" ? 0.5 : (0 as number)))?.reduce((a, b) => a + b, 0) || 0;

  const performAction = (action: Action, code?: string) => {
    setUploading(true);
    api.puzzles.performAction(currentPuzzle?.puzzleId || 0, action, code, {
      success: (result) => {
        if (result.result === "ERROR") alerts.error("Při provádění akce došlo k chybě.");
        if (result.result === "INVALID CODE") alerts.error("Poskytnuté heslo není správné");
        if (result.result === "INVALID STATE") alerts.error("Akce není povolená");
        if (result.result === "SUCCESS") {
          setTeam(result.team);
          setCode("");
        }
      },
      error: console.log,
      finally: () => setUploading(false),
    });
  };

  const showHelp = () => {
    setUploading(true);
    api.puzzles.getHelp(currentPuzzle?.puzzleId || 0, {
      success: (help) => alerts.infoModal("Nápověda", help),
      error: () => alerts.error("Při načítání nápovědy došlo k chybě."),
      finally: () => setUploading(false),
    });
  };

  return (
    <Container maxWidth="md">
      <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <img src={logo} alt="aquacoin logo" style={{ maxWidth: 580, marginTop: 8 }} />
        {team ? (
          <>
            <div style={{ flexGrow: 1 }}></div>
            {finishedAll ? (
              <div style={{ textAlign: "center" }}>
                <CheckCircle color="primary" sx={{ fontSize: 120, marginBottom: 8 }} />
                <Typography variant="h4" textAlign="center">
                  Gratulujeme, dostali jste se na konec hry. Získali jste celkem {score} {score === 0 ? "bodů" : score !== Math.round(score) ? "bodu" : score === 1 ? "bod" : score < 5 ? "body" : "bodů"}.
                </Typography>
              </div>
            ) : (
              <>
                {currentPuzzle?.status === "unknown" ? (
                  <>
                    <Typography>{instructions || "Načítání polohy další šifry..."}</Typography>
                    <br />
                    <br />
                    <TextField value={code} onChange={(e) => setCode(removeAllNonAlphaCharacters(removeDiacritics(e.target.value)).toUpperCase())} label="Příchodový kód šifry" />
                    <br />
                    <br />
                    <Button disabled={uploading || code.length === 0} onClick={() => performAction("arrive", code)} fullWidth variant="contained" color="primary">
                      Odeslat
                    </Button>
                  </>
                ) : (
                  <>
                    {/* TODO puzzle title */}
                    <TextField value={code} onChange={(e) => setCode(removeAllNonAlphaCharacters(removeDiacritics(e.target.value)).toLocaleLowerCase())} label="Heslo (výsledek šifry)" />
                    <br />
                    <br />
                    <Button disabled={uploading || code.length === 0} onClick={() => performAction("solve", code)} fullWidth variant="contained" color="primary">
                      Odeslat
                    </Button>
                    <br />
                    <br />
                    {currentPuzzle?.status === "helpAsked" ? (
                      <Button disabled={uploading} onClick={showHelp} fullWidth variant="contained" color="info">
                        Zobrazit nápovědu
                      </Button>
                    ) : (
                      <Button
                        disabled={uploading}
                        onClick={() => {
                          alerts.confirmModal("Požádat o nápovědu", "Opravdu chcete požádat o nápovědu k šifře? Za vyřešení šifry s nápovědou dostanete pouze polovinu bodů, než kdybyste ji vyřešili bez ní.", () => performAction("help"));
                        }}
                        fullWidth
                        variant="contained"
                        color="warning"
                      >
                        Požádat o nápovědu
                      </Button>
                    )}

                    <br />
                    <Button
                      disabled={uploading}
                      onClick={() => {
                        alerts.confirmModal("Vzdát šifru", "Opravdu chcete vzdát šifru? Za šifru, kterou vzdáte, nedostanete žádné body.", () => performAction("dead"));
                      }}
                      fullWidth
                      variant="contained"
                      color="error"
                    >
                      Vzdát šifru
                    </Button>
                  </>
                )}
              </>
            )}
            <div style={{ flexGrow: 1 }}></div>
            <Stepper sx={{ marginTop: 4 }} connector={<CustomStepConnector />} activeStep={team.puzzlesStatus.filter((p) => p.status !== "unknown").length - 1}>
              {team.puzzlesStatus.map((p) => (
                <Step key={p.puzzleId}>
                  <StepIcon
                    icon={
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {p.status === "unknown" ? (
                          <DoNotDisturbOn sx={{ fontSize: 32 }} color="disabled" />
                        ) : p.status === "arrived" || p.status === "helpAsked" ? (
                          <ChangeCircle
                            sx={{
                              animation: "spin 2s linear infinite",
                              transformOrigin: "center center",
                              width: 32,
                              height: 32,
                            }}
                            color="warning"
                          />
                        ) : p.status === "solvedWithHelp" ? (
                          <Help sx={{ fontSize: 32 }} color="info" />
                        ) : p.status === "solved" ? (
                          <CheckCircle sx={{ fontSize: 32 }} color="primary" />
                        ) : (
                          <Cancel sx={{ fontSize: 32 }} color="error" />
                        )}
                      </Box>
                    }
                  />
                </Step>
              ))}
            </Stepper>
            <br />
            <br />
          </>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
              }}
            >
              <CircularProgress
                sx={{
                  animation: "spin 2s linear infinite",
                  transformOrigin: "center center",
                }}
                size={200}
              />
            </Box>
          </>
        )}
      </div>
    </Container>
  );
}

export default App;
